import { useState, useEffect } from 'react';
import { Share2 } from 'lucide-react';
import api from '../lib/api';
import axios from 'axios';
import { createShareCard, copyShareLink } from '../lib/share';
import EmailCaptureGate, { TeaserResult } from '../components/EmailCaptureGate';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function FriendChallenge() {
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [teaserResult, setTeaserResult] = useState(null);
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [challengeType] = useState('casino_iq');

  const token = localStorage.getItem('casinoiq_token');
  const isGuest = !token;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        let res;
        if (isGuest) {
          res = await axios.get(`${API_BASE}/api/tools/challenges/questions?type=${challengeType}`);
        } else {
          res = await api.get(`/tools/challenges/questions?type=${challengeType}`);
        }
        setQuestions(res.data.questions || []);
      } catch (e) {
        console.error('Failed to load questions');
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [isGuest, challengeType]);

  const handleAnswer = async (answerIndex) => {
    const newAnswers = [...answers, answerIndex];
    setAnswers(newAnswers);

    if (current + 1 < questions.length) {
      setCurrent(current + 1);
    } else {
      if (isGuest) {
        setShowEmailGate(true);
      } else {
        try {
          const res = await api.post('/tools/challenges/attempt', {
            challenge_type: challengeType, answers: newAnswers,
          });
          setResult(res.data);
        } catch (e) {
          console.error('Submit failed');
        }
      }
    }
  };

  const handleEmailSubmit = async (email) => {
    const res = await axios.post(`${API_BASE}/api/tools/challenges/guest-attempt`, {
      challenge_type: challengeType, answers, email,
    });
    setShowEmailGate(false);
    setTeaserResult(res.data);
  };

  const handleShare = async () => {
    if (!result) return;
    setSharing(true);
    const card = await createShareCard('friend_challenge', {
      score: result.score,
      correct: result.correct,
      total: result.total,
      percentile: result.percentile,
      share_text: result.share_text,
    });
    if (card) copyShareLink(card.share_url);
    setSharing(false);
  };

  if (loading) return <div className="p-8 text-center text-white/50">Loading challenge...</div>;

  if (teaserResult) {
    return <TeaserResult toolType="challenge" score={teaserResult.score} correct={teaserResult.correct} total={teaserResult.total} percentile={teaserResult.percentile} />;
  }

  if (showEmailGate) {
    return <EmailCaptureGate toolName="Casino IQ Challenge" onSubmit={handleEmailSubmit} />;
  }

  if (result) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div className="bg-[#1e2438] border border-white/10 rounded-2xl p-8 text-center">
          <p className="text-amber-400 text-sm font-medium mb-1">Casino IQ Score</p>
          <h2 className="text-4xl font-bold text-white mb-2" data-testid="challenge-score">{result.score}/100</h2>
          <p className="text-white/50 text-sm mb-4">You beat {result.percentile}% of players</p>
          <p className="text-white/60 text-sm">{result.correct}/{result.total} correct answers</p>
        </div>
        <div className="bg-[#1e2438] border border-white/10 rounded-2xl p-6">
          <h3 className="text-white font-semibold mb-4 text-sm">Answer Breakdown</h3>
          <div className="space-y-3">
            {(result.results || []).map((r, i) => (
              <div key={i} className={`p-3 rounded-xl border text-sm ${r.correct ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                <p className="text-white/80 mb-1">{r.question}</p>
                <p className={`text-xs ${r.correct ? 'text-green-400' : 'text-red-400'}`}>
                  {r.correct ? 'Correct' : `Wrong — Answer: ${r.correct_answer}`}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="challenge-share-btn"
            onClick={handleShare}
            disabled={sharing}
            className="flex-1 h-11 rounded-xl border border-white/10 text-white/70 text-sm hover:bg-white/5 flex items-center justify-center gap-2 transition-colors"
          >
            <Share2 className="h-4 w-4" /> {sharing ? 'Creating link...' : 'Share Your Score'}
          </button>
        </div>
      </div>
    );
  }

  if (!questions.length) return <div className="p-8 text-center text-white/50">No questions available</div>;

  const q = questions[current];
  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-bold text-white">Casino IQ Challenge</h1>
          <span className="text-white/40 text-sm">{current + 1}/{questions.length}</span>
        </div>
        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
        </div>
      </div>
      <div className="bg-[#1e2438] border border-white/10 rounded-2xl p-6 mb-4">
        <h2 className="text-lg font-semibold text-white mb-4" data-testid="challenge-question-text">{q.question}</h2>
        <div className="space-y-3">
          {q.options.map((opt, i) => (
            <button
              key={i}
              data-testid={`challenge-option-${i}`}
              onClick={() => handleAnswer(i)}
              className="w-full text-left px-4 py-3 bg-[#141824] border border-white/10 hover:border-amber-500/50 rounded-xl text-white/80 hover:text-white transition-colors text-sm"
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
